import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getTimeline,
  getTimelineFilters,
  postTimelineFilters,
} from './calendarService';
import { getTimelineFiltersKey } from '../../domain/fullCalendar/fullCalendarUseCase';

export const TIMELINE_FILTERS_QUERY_KEY = 'timelineFilters';
export const TIMELINE_QUERY_KEY = 'timeline';

/**
 * React Query mutation key for timeline filters POST (per page path).
 * @param {string} path - Page path
 * @param {string | null} [contexte] - The context from URL query parameters
 * @returns {import('@tanstack/react-query').MutationKey}
 */
export const timelineFiltersMutationKey = (path, contexte = null) => [
  TIMELINE_FILTERS_QUERY_KEY,
  path,
  contexte,
];

/**
 * Query hook for timeline filters
 * @param {string} path - Timeline path
 * @param {string | null} [contexte] - The context from URL query parameters
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<ApiManagerTimelineFilter, Error>} The timeline filters
 */
export const useTimelineFilters = (path, contexte = null, options = {}) => {
  /** @type {import('@tanstack/react-query').UseQueryOptions<ApiManagerTimelineFilter, Error>} */
  const queryOptions = {
    queryKey: [TIMELINE_FILTERS_QUERY_KEY, path, contexte],
    queryFn: () => getTimelineFilters(path, contexte),
    enabled: Boolean(path),
    ...options,
  };

  return useQuery(queryOptions);
};

/**
 * Query hook for timeline data
 * @param {string} path - Timeline path
 * @param {ApiManagerTimelineFilter | undefined} timelineFilters - Current timeline filters
 * @param {string | null} [contexte] - The context from URL query parameters
 * @param {object} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<ApiManagerTimeline, Error>} The timeline data
 */
export const useTimeline = (path, timelineFilters, contexte = null, options = {}) => {
  const filtersKey = getTimelineFiltersKey(timelineFilters);

  /** @type {import('@tanstack/react-query').UseQueryOptions<ApiManagerTimeline, Error>} */
  const queryOptions = {
    queryKey: [TIMELINE_QUERY_KEY, path, filtersKey, contexte],
    queryFn: () => getTimeline(path, contexte),
    enabled: Boolean(path) && Boolean(filtersKey),
    ...options,
  };

  return useQuery(queryOptions);
};

/**
 * Mutation hook for timeline filters
 * @param {string} path - Timeline path
 * @param {string | null} [contexte] - The context from URL query parameters
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiManagerTimelineFilter, Error, ApiManagerTimelineFiltersCreate, unknown>} [options]
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiManagerTimelineFilter, Error, ApiManagerTimelineFiltersCreate, unknown>}
 */
export const useTimelineFiltersMutation = (path, contexte = null, options = {}) => useMutation({
  mutationFn: (data) => postTimelineFilters(path, data, contexte),
  ...options,
});
